import { ApolloCache } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import { setAnswerPageConfig } from '/@contexts/answer-context';
import { useAppContext } from '/@contexts/global-app-context';
import { useApolloClient } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { getAnswerIdForAnswerEditSession } from '../answer/answer-service';
import { setCachedAnswerBeforeMutation } from '../answer/answer-viz-service';
import { useBachMutation } from '../bach/bach';
import {
    SetTokensDocument,
    SetTokensMutation,
    SetTokensMutationVariables,
    VisualOverridesPayloadInput,
} from '../generated/graphql-types';
import { isQueryCancellationEnabled } from './search-cancel.util';

const ANSWER_UPDATE_DEBOUNCE_MS = 1000;

export function useUpdateAnswerMutation(
    answerSessionId: string,
    includeTableData?: boolean,
    onUpdateStart?: () => void,
) {
    const [_updateAnswer, { cancelMutation }] = useBachMutation<
        SetTokensMutation,
        SetTokensMutationVariables
    >(
        answerSessionId,
        SetTokensDocument,
        {
            setVisualizationsLoading: true,
            shouldRefetchData: true,
            variables: {
                includeTableData,
            } as SetTokensMutationVariables,
        },
        undefined,
        undefined,
        // We need to send AC session info in the request, because we are trying to apply the
        // updates from the AC session onto the bach session. Before this we would have made some
        // DoSearch requests directly to AC, and bach does not know about that.
        true,
    );
    const client = useApolloClient();
    const { sessionService, featureFlags } = useAppContext();
    const updateAnswer = React.useCallback(
        (
            shouldShowAnswerLoader = true,
            visualOverrides?: VisualOverridesPayloadInput,
        ) => {
            // set Privous answer in cache
            setCachedAnswerBeforeMutation(client, answerSessionId);
            _updateAnswer({
                update: (cache: ApolloCache<SetTokensMutation>) => {
                    // Upon completing the answer request, we will get back the answer data but without
                    // updated tokens. As a result, Apollo will carry forward the old answer token data
                    // from its cache, resulting in an inconsistent state. This also leads to issues
                    // where these stale tokens get reapplied to the sage bar since we don't have any
                    // way to know they are actually stale and not new tokens sent from the backend. To
                    // avoid these issues, we clear the old answer token data from the cache.
                    const id = cache.identify({
                        id: getAnswerIdForAnswerEditSession(
                            cache,
                            answerSessionId,
                        ),
                        __typename: 'Answer',
                    });
                    try {
                        cache.modify({
                            id,
                            fields: {
                                phrases() {
                                    return null;
                                },
                                updateOnlyPhrases(): boolean {
                                    return false;
                                },
                            },
                        });
                    } catch (error) {
                        if (!error?.message?.includes('already recomputing')) {
                            throw error;
                        }
                    }
                },
                variables:
                    visualOverrides != null
                        ? ({ visualOverrides } as SetTokensMutationVariables)
                        : null,
                setVisualizationsLoading: shouldShowAnswerLoader,
            });
            onUpdateStart?.();
        },
        [_updateAnswer, answerSessionId, client, onUpdateStart],
    );

    const isCancellationEnabled = isQueryCancellationEnabled(
        sessionService,
        featureFlags,
    );
    const memoizedDebouncedUpdate = React.useMemo(
        () => _.debounce(updateAnswer, ANSWER_UPDATE_DEBOUNCE_MS),
        [updateAnswer],
    );

    React.useEffect(() => {
        if (!isCancellationEnabled) {
            return undefined;
        }
        return () => {
            memoizedDebouncedUpdate.cancel();
        };
    }, [isCancellationEnabled, memoizedDebouncedUpdate]);

    const updateAnswerWithDebounce = memoizedDebouncedUpdate;

    const cancelUpdateAnswer = React.useCallback(() => {
        if (!isCancellationEnabled) {
            return;
        }
        cancelMutation?.();
    }, [cancelMutation, isCancellationEnabled]);

    return {
        updateAnswer,
        updateAnswerWithDebounce,
        cancelUpdateAnswer,
    };
}
